const STORAGE_KEY = "viafoundry.dismissedBanners";

interface DismissedBanner {
  id: number;
  updatedAt: string;
}

/**
 * Get all dismissed banners from localStorage
 */
export function getDismissedBanners(): DismissedBanner[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      // Filter to only valid DismissedBanner objects
      return parsed.filter(
        (item): item is DismissedBanner =>
          typeof item === "object" &&
          item !== null &&
          typeof item.id === "number" &&
          typeof item.updatedAt === "string"
      );
    }
    return [];
  } catch (error) {
    // Handle localStorage errors (e.g., private browsing mode)
    console.warn("Failed to read dismissed banners from localStorage:", error);
    return [];
  }
}

/**
 * Check if a banner is dismissed (matches both id and updatedAt)
 */
export function isBannerDismissed(
  bannerId: number,
  updatedAt: string
): boolean {
  const dismissedBanners = getDismissedBanners();
  return dismissedBanners.some(
    banner => banner.id === bannerId && banner.updatedAt === updatedAt
  );
}

/**
 * Dismiss a banner by adding its ID and updatedAt to localStorage
 */
export function dismissBanner(bannerId: number, updatedAt: string): void {
  try {
    const dismissedBanners = getDismissedBanners();
    // Remove any existing entry for this banner ID (in case of update)
    const filtered = dismissedBanners.filter(banner => banner.id !== bannerId);
    // Add the new dismissal
    filtered.push({ id: bannerId, updatedAt });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    // Dispatch storage event to sync across tabs
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: STORAGE_KEY,
        newValue: JSON.stringify(filtered),
        storageArea: localStorage,
      })
    );
  } catch (error) {
    // Handle localStorage errors (e.g., private browsing mode, quota exceeded)
    console.warn("Failed to save dismissed banner to localStorage:", error);
  }
}
